import React, { useState, useEffect, useContext, useCallback } from "react";
import { AttributesContext } from "contexts/attributes";
import ReferenceField from "components/attributes/compare-table/reference-field";
import { AttributeTitle } from "components/attributes/compare-table/attribute-title";
import { useColumnTemplate } from "shared/hooks/use-column-template";
import { useHighlight } from "../../coding/coding-qa/hooks/use-highlight";
import ReadOnlyField from "./read-only-field";
import { QAColumn } from "components/coding/components/qa-column";

import { TaskContext } from "contexts/task";
import "./compare-table.scss";

const QaAttributeRow = ({
    attr,
    references,
    columnWidths,
    readOnlyOverride,
    roleLabels,
    qaAttributes,
    refetchCoderRoles,
    hasRedo,
    isReadOnlyAdd,
    isReadOnlyEdit,
    isReadOnlyDelete,
    isBrandVerified,
    isQa,
}) => {
    const gridTemplate = useColumnTemplate(references.length + roleLabels.length + 1, columnWidths, false);
    const { task } = useContext(TaskContext);
    const { add } = useContext(AttributesContext);
    const { highlightRow, highlightCell, highlightValues, highlightQaCell } = useHighlight(roleLabels, attr, qaAttributes);
    const [qaEdit, showQaEdit] = useState(false);

    useEffect(() => {
        roleLabels.forEach((role) => {
            highlightValues(role);
        });
    }, []);

    useEffect(() => {
        highlightQaCell();
    }, [qaAttributes]);

    const handleAddAttribute = useCallback(
        (e) => {
            e.preventDefault();
            showQaEdit(true);
            add(attr);
        },
        [add, attr]
    );

    const DisplayValues = ({ role }) => {
        if (role.type !== "qa") {
            return (
                <div className="coder-attributes-container">
                    <ReadOnlyField
                        id={attr.uuid}
                        value={role.attributes[attr.uuid]}
                        defaultValue={attr.defaultValue}
                        attribute={attr}
                        wipKey={role.codingWipKey}
                        readOnlyOverride={readOnlyOverride}
                        refetchCoderRoles={refetchCoderRoles}
                        taskType={task.taskType}
                        roleType={role.type}
                        roleLabel={role.label}
                    />
                </div>
            );
        }

        return (
            <QAColumn
                roleLabels={roleLabels}
                role={role}
                attr={attr}
                readOnlyOverride={readOnlyOverride}
                highlightRow={highlightRow}
                highlightCell={highlightCell}
                qaAttributes={qaAttributes}
                refetchCoderRoles={refetchCoderRoles}
                qaEdit={qaEdit}
                showQaEdit={showQaEdit}
                hasRedo={hasRedo}
                isReadOnlyEdit={isReadOnlyEdit}
            />
        );
    };

    const highlightColor = highlightRow && !hasRedo ? "-highlight" : "";

    return (
        <div
            id={attr.key}
            className={`product-compare-row -add-padding ${highlightColor}`}
            style={gridTemplate}
        >
            <AttributeTitle
                title={attr.name}
                attr={attr}
                addAttribute={handleAddAttribute}
                codingWipKey={task.context.codingWipKey}
                isCompleted={task.isCompleted || hasRedo}
                isReadOnlyAdd={isReadOnlyAdd}
                isReadOnlyDelete={isReadOnlyDelete}
                isBrandVerified={isBrandVerified}
                isQa={isQa}
            />
            {roleLabels.map((role, index) => (
                <DisplayValues
                    role={role}
                    index={index}
                    key={`AIF_${attr.uuid}_${role.label}_ref${index}`}
                />
            ))}

            {references.map((r, i) => {
                return (
                    <ReferenceField
                        key={`ROF_${attr.uuid}_ref${i}`}
                        value={r[attr.uuid]}
                        attribute={attr}
                        wipKey={r.key}
                        readOnlyOverride={readOnlyOverride}
                        isCompleted={task.isCompleted}
                        upc={r.upc}
                    />
                );
            })}
        </div>
    );
};

export default QaAttributeRow;
